import { useState, useEffect, useCallback } from 'react';
import comercialConsolidadoService from 'features/comercial/services/comercialConsolidadoService';
import { getCompanyContext } from 'utils/setCompanyContext';

const useComercialConsolidado = (filtrosData = null, options = {}) => {
  const enabled = options.enabled !== false;
  const [dadosComerciais, setDadosComerciais] = useState({
    resumo: {
      total_vendas: 0,
      projecao_total_vendas: null,
      total_receita_vendas: 0,
      projecao_receita_vendas: null,
      ticket_medio: 0
    },
    evolucao_mensal: [],
    lista_inadimplencia: [],
    ranking_vendedores: [],
    clientes_os_aberta_72h: [],
    clientes_assinatura: [],
    novos_clientes: [],
    empresas: []
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [isConsolidated, setIsConsolidated] = useState(false);

  const carregarDadosComerciais = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Obter contexto da empresa selecionada
      const companyContext = getCompanyContext();
      const isConsolidatedValue = companyContext?.isConsolidated || 
                                companyContext?.isGroup || 
                                (companyContext?.companies?.length > 1);
      
      setIsConsolidated(isConsolidatedValue);
      
      // Preparar filtros de data
      const filtros = filtrosData ? {
        data_inicio: filtrosData.start,
        data_fim: filtrosData.end
      } : {};
      
      if (isConsolidatedValue && companyContext?.companies?.length > 1) {
        // Modo consolidado - buscar dados de múltiplas empresas
        const companyNames = companyContext.companies.map(company => company.name);
        
        const resultado = await comercialConsolidadoService.buscarDadosConsolidados(companyNames, filtros);
        
        if (resultado.success && resultado.data) {
          setDadosComerciais(resultado.data);
          setUltimaAtualizacao(new Date());
        } else {
          throw new Error('Erro ao carregar dados comerciais consolidados');
        }
      } else {
        // Modo individual - buscar dados de uma empresa específica
        const empresa = companyContext?.companies?.[0]?.name || 'MOV Fibra';
        
        const resultado = await comercialConsolidadoService.buscarDadosConsolidados([empresa], filtros);
        
        if (resultado.success && resultado.data) {
          setDadosComerciais(resultado.data);
          setUltimaAtualizacao(new Date());
        } else {
          // Limpar dados em caso de erro
          setDadosComerciais({
            resumo: {
              total_vendas: 0,
              projecao_total_vendas: null,
              total_receita_vendas: 0,
              projecao_receita_vendas: null,
              ticket_medio: 0
            },
            evolucao_mensal: [],
            lista_inadimplencia: [],
            ranking_vendedores: [],
            clientes_os_aberta_72h: [],
            clientes_assinatura: [],
            novos_clientes: [],
            empresas: []
          });
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados comerciais:', error);
      setError('Não foi possível carregar os dados comerciais. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
      // Disparar evento quando loading termina
      window.dispatchEvent(new CustomEvent('company-change-complete'));
    }
  }, [enabled, filtrosData]);

  // Monitorar mudanças no contexto da empresa para atualizar isConsolidated
  useEffect(() => {
    if (!enabled) {
      setIsConsolidated(false);
      setInitialLoading(false);
      return;
    }

    const companyContext = getCompanyContext();
    const isConsolidatedValue = companyContext?.isConsolidated || 
                              companyContext?.isGroup || 
                              (companyContext?.companies?.length > 1);
    
    setIsConsolidated(isConsolidatedValue);
  }, [enabled]);

  // Serializar filtrosData para usar como dependência (evita re-renders desnecessários)
  const filtrosDataKey = filtrosData ? `${filtrosData.start}-${filtrosData.end}-${filtrosData.presetId || ''}` : 'default';

  // Carregar dados iniciais e reagir a mudanças de empresa e filtros
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setInitialLoading(false);
      return undefined;
    }

    carregarDadosComerciais();
    
    // Listener para mudanças de empresa
    const handleCompanyChange = () => {
      carregarDadosComerciais();
    };
    
    window.addEventListener('company-changed', handleCompanyChange);
    
    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [enabled, filtrosDataKey, carregarDadosComerciais]);

  const atualizarDados = () => {
    carregarDadosComerciais();
  };

  return {
    dadosComerciais,
    loading,
    initialLoading,
    error,
    ultimaAtualizacao,
    isConsolidated,
    atualizarDados
  };
};

export default useComercialConsolidado;
